import type { Location } from "../../api/types";
import { SegmentedControl } from "../ui/SegmentedControl";

export const ALL_SHOPS = "" as const;

/**
 * G-94 — the control that tells this page which shop it is talking about.
 *
 * The inventory page and the shop dashboard were one screen split in half:
 * the inventory page could say what stock exists, the shop page could act on
 * it, and the only thing the shop page had that the inventory page lacked was
 * a chosen shop. Every verb needs one — receive INTO what, waste FROM where —
 * so this picker is the missing input, not a filter.
 *
 * It renders only when there is a choice to make (G-132: a segmented switch). Managers and florists are
 * scoped server-side to their assigned shop (`locations.service.ts`
 * `listLocationsForStaff`), so the list they receive holds exactly one entry
 * and a dropdown with one option would be furniture: they get nothing at all.
 *
 * The value lives in the URL (`?locationId=`), not in component state, for
 * three reasons that are all load-bearing: `InventoryPanel` already reads that
 * param, G-83's create-then-receive flow arrives carrying it, and G-92's phone
 * action sheet deep-links it. One source, three callers.
 */
export function InventoryShopPicker({
  locations,
  value,
  onChange,
}: {
  locations: Location[];
  /** The selected shop id, or `ALL_SHOPS` for the roll-up. */
  value: string;
  onChange: (locationId: string) => void;
}) {
  const active = locations.filter((l) => l.isActive);

  // One shop is no choice at all — and its staff know which shop they work
  // in (owner, 2026-10-09: the "Stock at <shop>" line was noise). Nothing.
  if (active.length <= 1) return null;

  /* G-132: a one-tap switch, "All" first, instead of a dropdown that hid the
     shop names behind a tap. */
  return (
    <div className="inv-shop-switch">
      <SegmentedControl
        ariaLabel="Shop"
        value={value}
        onChange={onChange}
        options={[
          { value: ALL_SHOPS, label: "All" },
          ...active.map((loc) => ({ value: loc.id, label: loc.name })),
        ]}
      />
    </div>
  );
}
